/**
 * Daily Prompt Card With Countdown Component
 * Displays the daily writing prompt with a countdown timer until the next refresh.
 * Uses the prompt's stored imageUrl so the image stays the same across server restarts (e.g. Railway rebuilds).
 */

import React, { useState, useEffect } from 'react';
import { useTimeUntilMidnightCountdown } from '../../hooks/useTimeUntilMidnightCountdown';
import { formatPromptDate } from '../../utils/formatPromptDate';
import './DailyPromptCardWithCountdown.css';

/**
 * Fetch the daily background image from the server (fallback when prompt has no imageUrl)
 */
const fetchDailyImage = async () => {
  try {
    const response = await fetch('/api/prompts/daily-image');
    if (!response.ok) {
      throw new Error('Failed to fetch daily image');
    }
    const data = await response.json();
    return data.url;
  } catch (error) {
    console.error('Error fetching daily image:', error);
    return null;
  }
};

/**
 * Load a Google Font by injecting a link tag
 */
function loadGoogleFont(fontFamily) {
  if (!fontFamily || typeof document === 'undefined') return;
  const id = `font-${fontFamily.replace(/\s+/g, '-')}`;
  if (document.getElementById(id)) return;
  const link = document.createElement('link');
  link.id = id;
  link.rel = 'stylesheet';
  link.href = `https://fonts.googleapis.com/css2?family=${encodeURIComponent(fontFamily)}&display=swap`;
  document.head.appendChild(link);
}

const DailyPromptCardWithCountdown = ({ prompt, responseCount, nextRefresh, onRefresh, font }) => {
  const timeUntilRefresh = useTimeUntilMidnightCountdown(nextRefresh, onRefresh);
  const [backgroundImage, setBackgroundImage] = useState(null);
  const [imageLoaded, setImageLoaded] = useState(false);

  const fontFamily = font || prompt?.font || null;

  // Use prompt.imageUrl (persisted in DB) so the image survives server restarts (e.g. Railway rebuilds)
  useEffect(() => {
    const imageUrl = prompt?.imageUrl;
    if (imageUrl) {
      setBackgroundImage(imageUrl);
      const img = new Image();
      img.onload = () => setImageLoaded(true);
      img.src = imageUrl;
      return;
    }
    fetchDailyImage().then(url => {
      if (url) {
        setBackgroundImage(url);
        const img = new Image();
        img.onload = () => setImageLoaded(true);
        img.src = url;
      }
    });
  }, [prompt?.imageUrl]);

  useEffect(() => {
    if (fontFamily) {
      loadGoogleFont(fontFamily);
    }
  }, [fontFamily]);

  if (!prompt) return null;

  const imageStyle = backgroundImage && imageLoaded
    ? { backgroundImage: `url(${backgroundImage})` }
    : {};

  const textContainerStyle = fontFamily
    ? { fontFamily: `"${fontFamily}", serif` }
    : {};

  return (
    <div className="prompt-card">
      {/* Top info bar */}
      <div className="prompt-info-bar">
        <span className="prompt-badge">
          Prompt of the Day{prompt.date && ` · ${formatPromptDate(prompt.date)}`}
        </span>
        <span className="prompt-info-bar-brand">writeflow.ink</span>
        <div className="prompt-info-right">
          {responseCount !== undefined && (
            <span className="response-count">{responseCount} responses</span>
          )}
          {timeUntilRefresh && (
            <span className="refresh-countdown">{timeUntilRefresh}</span>
          )}
        </div>
      </div>

      {/* Bottom image section with text */}
      <div 
        className={`prompt-image-section ${backgroundImage && imageLoaded ? 'has-image' : ''}`}
        style={imageStyle}
      >
        <div className="prompt-text-container" style={textContainerStyle}>
          <p className="prompt-emotion">{prompt.emotion}</p>
          <p className="prompt-connector">{prompt.connector}</p>
          <p className="prompt-setting">{prompt.setting}</p>
        </div>
      </div>
    </div>
  );
};

export default DailyPromptCardWithCountdown;
